import { useEffect, useRef, useState } from 'react';
import { Trash2, Upload, Music, Search } from 'lucide-react';
import { musicApi, MusicTrack } from '@/api/music';
import { mediaApi } from '@/api/media';

/**
 * Admin UI for Phase 11 music library: upload audio + metadata; delete.
 */
export default function AdminMusic() {
  const [tracks, setTracks] = useState<MusicTrack[]>([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');

  const [title, setTitle] = useState('');
  const [artist, setArtist] = useState('');
  const [audioFile, setAudioFile] = useState<File | null>(null);
  const [coverUrl, setCoverUrl] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const audioRef = useRef<HTMLInputElement>(null);

  const load = async () => {
    setLoading(true);
    try {
      const res = await musicApi.list({ sort: 'recent', q: q.trim() || undefined, limit: 100 });
      setTracks(res.items);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [q]);

  const detectDuration = (file: File): Promise<number> =>
    new Promise((resolve, reject) => {
      const audio = new Audio();
      const url = URL.createObjectURL(file);
      audio.addEventListener('loadedmetadata', () => {
        URL.revokeObjectURL(url);
        resolve(Math.round(audio.duration));
      });
      audio.addEventListener('error', () => {
        URL.revokeObjectURL(url);
        reject(new Error('Не удалось прочитать длительность'));
      });
      audio.src = url;
    });

  const submit = async () => {
    setError(null);
    if (!audioFile || !title.trim() || !artist.trim()) {
      setError('Нужны файл, название и исполнитель');
      return;
    }
    setSubmitting(true);
    try {
      const durationSec = await detectDuration(audioFile);
      if (durationSec < 1 || durationSec > 600) {
        throw new Error('Длина трека должна быть 1–600 сек');
      }
      const media = await mediaApi.upload(audioFile);
      await musicApi.create({
        title: title.trim(),
        artist: artist.trim(),
        mediaFileId: media.id,
        coverUrl: coverUrl.trim() || null,
        durationSec,
      });
      setTitle(''); setArtist(''); setAudioFile(null); setCoverUrl('');
      if (audioRef.current) audioRef.current.value = '';
      await load();
    } catch (e: any) {
      setError(e?.response?.data?.message ?? e?.message ?? 'Ошибка');
    } finally {
      setSubmitting(false);
    }
  };

  const remove = async (id: string) => {
    if (!confirm('Удалить трек? Stories на нём останутся, но без музыки.')) return;
    await musicApi.remove(id);
    setTracks((t) => t.filter((x) => x.id !== id));
  };

  return (
    <div className="p-6 max-w-4xl">
      <h1 className="text-xl font-bold text-surface-50 mb-4 flex items-center gap-2">
        <Music size={20} /> Music library
      </h1>

      <div className="bg-surface-900 border border-surface-800 rounded-lg p-4 mb-6 space-y-3">
        <div className="text-sm text-surface-300 font-semibold">Новый трек</div>
        <div className="grid grid-cols-2 gap-3">
          <input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Название"
            className="bg-surface-800 rounded-lg px-3 py-2 text-sm"
          />
          <input
            value={artist}
            onChange={(e) => setArtist(e.target.value)}
            placeholder="Исполнитель"
            className="bg-surface-800 rounded-lg px-3 py-2 text-sm"
          />
          <input
            value={coverUrl}
            onChange={(e) => setCoverUrl(e.target.value)}
            placeholder="Обложка URL (необязательно)"
            className="col-span-2 bg-surface-800 rounded-lg px-3 py-2 text-sm"
          />
          <input
            ref={audioRef}
            type="file"
            accept="audio/*"
            onChange={(e) => setAudioFile(e.target.files?.[0] ?? null)}
            className="col-span-2 text-xs text-surface-300"
          />
        </div>
        {error && <div className="text-red-400 text-xs">{error}</div>}
        <button
          onClick={submit}
          disabled={submitting}
          className="inline-flex items-center gap-2 bg-brand-600 hover:bg-brand-500 disabled:opacity-40 px-4 py-2 rounded-lg text-sm"
        >
          <Upload size={14} /> {submitting ? 'Загрузка…' : 'Добавить трек'}
        </button>
      </div>

      <div className="relative mb-3">
        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-surface-500" />
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Поиск по библиотеке…"
          className="w-full bg-surface-900 border border-surface-800 rounded-lg pl-9 pr-3 py-2 text-sm"
        />
      </div>

      <div className="bg-surface-900 border border-surface-800 rounded-lg overflow-hidden">
        {loading ? (
          <div className="p-6 text-center text-surface-500">Загрузка…</div>
        ) : tracks.length === 0 ? (
          <div className="p-6 text-center text-surface-500">Треков нет</div>
        ) : (
          tracks.map((t) => (
            <div key={t.id} className="flex items-center gap-3 px-4 py-3 border-b border-surface-800 last:border-b-0">
              <div className="w-10 h-10 rounded-lg bg-surface-800 flex items-center justify-center overflow-hidden">
                {t.coverUrl ? (
                  <img src={t.coverUrl} alt="" className="w-full h-full object-cover" />
                ) : (
                  <Music size={16} />
                )}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-surface-50 truncate">{t.title}</div>
                <div className="text-xs text-surface-400 truncate">{t.artist} · {formatDur(t.durationSec)} · {t.usageCount} uses</div>
              </div>
              <audio src={t.audioUrl} controls className="h-8 w-48" preload="none" />
              <button
                onClick={() => remove(t.id)}
                className="text-red-400 hover:text-red-300 p-1"
                title="Удалить"
              >
                <Trash2 size={16} />
              </button>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

function formatDur(sec: number) {
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return `${m}:${s.toString().padStart(2, '0')}`;
}
